import { FormControl, MenuItem, Select, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';

import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { styled } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import React, { useState } from 'react';
import { AiFillEdit } from 'react-icons/ai';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { MdDelete } from 'react-icons/md';

// interface
import type { IAlertFeedBackDetails } from '@/components/feedback/alert';
import { FeedbackAlert } from '@/components/feedback/alert';
import type { IModalConfirmationDetails } from '@/components/modal/confirmation';
import { ConfirmationModal } from '@/components/modal/confirmation';
import type { PageInfo } from '@/generated/graphql';
import { useLimitDeleteGameDrawtimeLimitMutation } from '@/generated/graphql';
import { ChangePageType } from '@/shared/enums';
import type { IGameDrawtimelimitTableData, IGameDrawtimeLimitQueryVariable, IGameDrawTime } from '@/shared/interfaces';

// enums
// graphql
// components
import { EditNumberSalesLimitModal } from './edit-game-drawtime-limit';

interface TablePageLabel {
  start: number;
  end: number;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof IGameDrawtimelimitTableData | '';
  label: string;
  numeric: boolean;
}

const StyledTableCell = styled(TableCell)(({}) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#FFFFFF',
    color: '#6B6B6B',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  '&:active tableCell.Mui-active': {
    color: '#333',
  },
}));

const StyledTextField = styled(TextField)(({}) => ({
  '& label, & label.Mui-focused': {
    color: '#333',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#333',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: '#333',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#333',
    },
  },
  input: {
    color: '#333',
  },
}));

const StyledTableRow = styled(TableRow)(({}) => ({
  '& .MuiTableCell-root': {
    fontFamily: 'Open Sans,Roboto',
    fontWeight: 600,
    fontSize: 15,
    pt: 15,
    pb: 15,
    pl: 15,
  },

  '&:nth-of-type(odd)': {
    backgroundColor: '#E8F0F7',
    fontFamily: 'Poppins',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#E0EAF5',
    fontFamily: 'Poppins',
  },

  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const headCells: readonly HeadCell[] = [
  {
    id: 'drawtime',
    numeric: false,
    disablePadding: true,
    label: 'Game Drawtime',
  },
  {
    id: 'limit',
    numeric: false,
    disablePadding: true,
    label: 'Current Limit',
  },
  {
    id: 'startDate',
    numeric: false,
    disablePadding: true,
    label: 'Start Date',
  },
  {
    id: 'endDate',
    numeric: false,
    disablePadding: true,
    label: 'End Date',
  },
  {
    id: 'createdAt',
    numeric: false,
    disablePadding: true,
    label: 'Created On',
  },
  {
    id: '',
    numeric: false,
    disablePadding: true,
    label: '',
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IGameDrawtimelimitTableData) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    order,
    orderBy,

    onRequestSort,
  } = props;
  const createSortHandler = (property: keyof IGameDrawtimelimitTableData) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <StyledTableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ pl: 2, fontWeight: 600 }}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={headCell.id === '' ? undefined : createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </StyledTableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  searchHandler: (value: string) => void;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { numSelected, searchHandler } = props;

  return (
    <Toolbar
      sx={{
        backgroundColor: '#93c1f2',
        color: '#222',

        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}>
      <Typography sx={{ fontWeight: 400, flex: '1 1 100%', fontSize: 18, color: '#111' }} variant="h6" id="tableTitle" component="div">
        Individual Limits
      </Typography>
      <FormControl sx={{ ml: 2, mb: 1, mt: 1, mr: 1, width: 300 }}>
        <StyledTextField
          onChange={(e) => {
            searchHandler(e.target.value);
          }}
          id="filled-search"
          label="Search field"
          type="search"
          variant="outlined"
          size="small"
        />
      </FormControl>
    </Toolbar>
  );
};

interface limitTableTableProps {
  rows: readonly IGameDrawtimelimitTableData[];
  setGameDrawtimeLimitQueryVariable: React.Dispatch<React.SetStateAction<IGameDrawtimeLimitQueryVariable>>;
  gameDrawtimeLimitQueryVariable: IGameDrawtimeLimitQueryVariable;
  totalCount: number;
  pageInfo: PageInfo | undefined;
  refetchData: () => void;
  gameDrawTimes: IGameDrawTime[];
}

export function GameDrawtimeLimitTable(props: limitTableTableProps) {
  const { rows, gameDrawtimeLimitQueryVariable, setGameDrawtimeLimitQueryVariable, totalCount, pageInfo, refetchData, gameDrawTimes } =
    props;
  const [searchedLimit, setSearchedLimit] = useState<IGameDrawtimelimitTableData[]>([]);
  const [searchWord, setSearchWord] = useState('');

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof IGameDrawtimelimitTableData>('limit');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [page] = React.useState(0);
  const [dense] = React.useState(true);
  const [rowsPerPage] = React.useState(5);

  const [pageLabel, setPageLabel] = useState<TablePageLabel>({
    start: 1,
    end: gameDrawtimeLimitQueryVariable.first,
  });

  // edit limit modal

  const [deleteGameLimit] = useLimitDeleteGameDrawtimeLimitMutation();

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState<IGameDrawtimelimitTableData>({
    limit: 0,
    startDate: '',
    endDate: '',
    id: '',
    drawtime: '',
    gameDrawtimeId: '',
    createdAt: '',
  });
  // delete limit modal
  const [confirmationDeleteModal, setConfirmationDeleteModal] = useState<IModalConfirmationDetails>({
    isOpen: false,
    title: 'Confirm Result',
    messages: [],
  });
  // for success feedback
  const [successDetails, setSuccessDetails] = useState<IAlertFeedBackDetails>({
    isOpen: false,
    message: '',
    type: 'success',
  });

  const clickEdit = (limit: IGameDrawtimelimitTableData) => {
    setIsLimitModalOpen(true);
    setSelectedLimit(limit);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IGameDrawtimelimitTableData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const searchHandler = (value: string) => {
    setSearchWord(value);
    type ILimitTableDataKey = keyof IGameDrawtimelimitTableData;
    const searchKeys: ILimitTableDataKey[] = ['drawtime', 'limit'];

    const newLimit = rows.filter((row) => searchKeys.some((searchKey) => row[searchKey]?.toString().includes(value)));
    setSearchedLimit(newLimit);
  };

  const handleChangePage = (type: ChangePageType) => {
    if (type === ChangePageType.NEXT && pageInfo?.endCursor) {
      // next
      setGameDrawtimeLimitQueryVariable({
        ...gameDrawtimeLimitQueryVariable,
        after: pageInfo.endCursor,
        before: undefined,
      });
      let end = pageLabel.end + gameDrawtimeLimitQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start + gameDrawtimeLimitQueryVariable.first, end });
    } else if (type === ChangePageType.PREV && pageInfo?.startCursor) {
      // prev
      setGameDrawtimeLimitQueryVariable({
        ...gameDrawtimeLimitQueryVariable,
        before: pageInfo.startCursor,
        after: undefined,
      });
      let end = pageLabel.end - gameDrawtimeLimitQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start - gameDrawtimeLimitQueryVariable.first, end });
    }
  };

  const handleChangeRowsPerPage = (value: string | number) => {
    const intValue = parseInt(value.toString(), 10);

    setGameDrawtimeLimitQueryVariable({
      ...gameDrawtimeLimitQueryVariable,
      first: intValue,
    });
  };

  const clickDeleteBtn = (limit: IGameDrawtimelimitTableData) => {
    setConfirmationDeleteModal({
      isOpen: true,
      title: 'Confirm Result',
      messages: ['Are you sure you want to delete this limit?'],
    });
    setSelectedLimit(limit);
  };

  const cofirmedDelete = () => {
    deleteGameLimit({
      variables: { id: selectedLimit.id },
      onCompleted: () => {
        refetchData();
        setConfirmationDeleteModal({
          isOpen: false,
          title: 'Confirm Result',
          messages: [],
        });
        setSuccessDetails({
          isOpen: true,
          message: 'Limit delete successfully',
          type: 'success',
        });
      },
    });
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <>
      <Box sx={{ width: '100%', mt: 0 }}>
        <Paper sx={{ width: '100%', mb: 2, background: '#93c1f2', color: '#585858' }}>
          <EnhancedTableToolbar numSelected={selected.length} searchHandler={searchHandler} />
          <TableContainer>
            <Table sx={{ minWidth: 750, background: '#ccd8e5' }} aria-labelledby="tableTitle" size={dense ? 'small' : 'medium'}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                    rows.slice().sort(getComparator(order, orderBy)) */}
                {(searchWord.length >= 1 ? searchedLimit : rows).map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <StyledTableRow
                      sx={{
                        pl: 2,
                        fontWeight: 500,
                        fontFamily: 'Poppins! important',
                      }}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}>
                      <TableCell align="left">{row.drawtime}</TableCell>
                      <TableCell align="left">{row.limit}</TableCell>
                      <TableCell align="left">{row.startDate}</TableCell>
                      <TableCell align="left">{row.endDate}</TableCell>
                      <TableCell align="left">{row.createdAt}</TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="delete" color="error" onClick={() => clickDeleteBtn(row)}>
                          <MdDelete />
                        </IconButton>
                        <IconButton aria-label="edit" color="primary" onClick={() => clickEdit(row)}>
                          <AiFillEdit />
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <StyledTableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}>
                    <TableCell colSpan={6} />
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* #6591bd */}
          <Box
            sx={{
              // backgroundColor: '#93c1f2',
              color: '#111',
              mt: 1,
              justifyContent: 'end',
              alignItems: 'center',
              display: 'flex',
            }}>
            <Typography variant="body2" mr={4} gutterBottom>
              Rows per page:
            </Typography>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 50 }}>
              <Select
                className="bg-white"
                sx={{ color: '#111', fontWeight: 600, px: 2 }}
                onChange={(e) => {
                  handleChangeRowsPerPage(e.target.value);
                }}
                size="small"
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={gameDrawtimeLimitQueryVariable.first}
                label="Row">
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" mx={4} gutterBottom>
              {pageLabel.start} - {pageLabel.end} of {totalCount}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(ChangePageType.PREV)}
              disabled={pageInfo?.hasPreviousPage ? false : true}
              size={'small'}
              sx={{ mr: 2 }}
              aria-label="prev">
              <IoIosArrowBack />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(ChangePageType.NEXT)}
              disabled={pageInfo?.hasNextPage ? false : true}
              size={'small'}
              aria-label="next">
              <IoIosArrowForward />
            </IconButton>
          </Box>
        </Paper>
      </Box>
      <EditNumberSalesLimitModal
        isLimitModalOpen={isLimitModalOpen}
        setIsLimitModalOpen={setIsLimitModalOpen}
        gameDrawtimeLimit={selectedLimit}
        refetchData={refetchData}
        gameDrawTimes={gameDrawTimes}
      />
      <ConfirmationModal
        modalDetails={confirmationDeleteModal}
        setModalDetails={setConfirmationDeleteModal}
        confirm={cofirmedDelete}
        confirmText={'Delete'}
      />
      <FeedbackAlert successDetails={successDetails} setSuccessDetails={setSuccessDetails} />
    </>
  );
}
